/* eslint-disable max-lines-per-function */
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { overquotaNewUnsub } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { updateBillingDataRequest } from 'reactApp/modules/billingData/billingData.module';
import { BuySubscriptionActions } from 'reactApp/modules/buySubscription/buySubscription.module';
import { setDiscountInOutflow } from 'reactApp/modules/buySubscription/buySubscription.saga';
import { BuyNotifications } from 'reactApp/modules/buySubscription/buySubscription.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { loadCard } from 'reactApp/modules/creditCard/creditCard.saga';
import { getHomeFolderItems } from 'reactApp/modules/home/home.selectors';
import { loadHomeFolder } from 'reactApp/modules/home/homeCore.saga';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import {
    getWebOutflowOffers,
    processWebOuflowOfferList,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/subscriptionRenewal.offers.web';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { ESubscriptionModalAction } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { store } from 'reactApp/store';
import { closeCardModal, renderCardModal } from 'reactApp/ui/AddCardModal/AddCardModal.helpers';
import { discountInOutflowTypedRadarName } from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { closeDiscountInOutflowSaveModal } from 'reactApp/ui/DiscountInOutflow/DiscountInOutflowSaveModal/DiscountInOutflowSaveModal.helpers';
import { createGaSender, sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { channel } from 'redux-saga';
import { call, cancel, put, putResolve, take, takeEvery } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

import { logError } from './subscriptionRenewal.helpers';

const sendBillingGa = createGaSender('billing');

function* handleCancelAutoRenew({
    payload: { sendAnalytics, product, subscription },
}: ReturnType<typeof SubscriptionRenewalActions.cancelAutoRenew>) {
    const { isTrial } = subscription;
    const outflowChannel = channel<boolean>();
    const cancelOutflow = () => outflowChannel.close();
    const continueOutflow = () => outflowChannel.put(true);

    sendBillingGa('autorenew-cancel-click');
    // tempexp_16481-next-line
    sendAnalytics?.(ESubscriptionModalAction.openCancelAutopayment);
    emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY, { isTrial });

    const outflowOffers = yield getWebOutflowOffers(subscription);

    // tempexp_19963-start
    if (overquotaNewUnsub.enable) {
        const homeItems = yield select(getHomeFolderItems);
        if (homeItems.length === 0) {
            yield loadHomeFolder({ id: ROOT_FOLDER_ID, isFolder: true });
        }
    }
    // tempexp_19963-end

    yield processWebOuflowOfferList(
        outflowOffers,
        {
            product,
            subscription,
            outflowContext: {},
            sendAnalytics,
            cancelOutflow,
            continueOutflow,
        },
        outflowChannel
    );

    yield cancel();
}

function* handleAddAutoRenew({
    payload: { sendAnalytics, card, subscription, onSuccess },
}: ReturnType<typeof SubscriptionRenewalActions.addAutoRenew>) {
    sendBillingGa('autorenew-click');
    sendAnalytics?.(ESubscriptionModalAction.turnOnAutopayment);

    if (!card) {
        return yield addCard(subscription.id, onSuccess, sendAnalytics);
    }

    yield addAutoRenew(subscription.id, onSuccess);
}

// tempexp_18907-start
function* handleAddAutoRenewWithDiscount(action: ReturnType<typeof SubscriptionRenewalActions.addAutoRenewWithDiscount>) {
    const { subscription, discountModalType: type } = action.payload;
    const isSuccessCancel = yield* handleAddAutoRenew(action);
    if (!isSuccessCancel) {
        return;
    }
    const isSuccessSetDiscount = yield* setDiscountInOutflow(
        BuySubscriptionActions.setDiscountInOutflow({ subscriptionId: subscription.id })
    );
    if (isSuccessSetDiscount) {
        sendPaymentGa({
            action: ESubscriptionModalAction.turnOnAutopaymentWithDiscount,
            eventCategory: ECategoryGa.payment,
            name: discountInOutflowTypedRadarName(type),
        });
        emitAnalyticEvent(AnalyticEventNames.DISCOUNT_MODAL_ENABLE, { type });
    }
    closeDiscountInOutflowSaveModal();
}
// tempexp_18907-end

function* addCard(subscriptionId: string, onSuccessAutorenew?: () => void, sendAnalytics?: (action: ESubscriptionModalAction) => void) {
    const autoRenewChannel = channel();
    yield renderCardModal({
        onSuccess() {
            sendBillingGa('addCardClick', 'subscriptionModal');
            sendAnalytics?.(ESubscriptionModalAction.addNewCard);
            store.dispatch(
                CardActions.updateCard({
                    showNotifications: true,
                    onSuccess: () => autoRenewChannel.put(true),
                })
            );
        },
        onClose: () => {
            autoRenewChannel.close();
            closeCardModal();
        },
        onCance() {
            autoRenewChannel.close();
            sendAnalytics?.(ESubscriptionModalAction.cancelNewCard);
        },
        onUnlinkCard() {
            autoRenewChannel.close();
            store.dispatch(CardActions.updateCard({ isSilent: true }));
        },
    });

    if (yield take(autoRenewChannel)) {
        return yield addAutoRenew(subscriptionId, onSuccessAutorenew);
    }
}

function* addAutoRenew(subscriptionId: string, onSuccess?: () => void) {
    const autoRenewChannel = channel();
    try {
        yield putResolve(yield call(BuySubscriptionActions.addAutorenew, subscriptionId));
        sendBillingGa('autorenew-success');
        autoRenewChannel.put(true);
    } catch (error) {
        sendXray(['web', 'subscription', 'autorenew', 'error']);
        logError(error);
        yield put(
            showSnackbarAction({
                text: 'Не удалось подключить автоплатёж',
                type: SnackbarTypes.failure,
                id: BuyNotifications.autoRenewFailure,
                closable: true,
            })
        );
        autoRenewChannel.close();
        yield cancel();
    }

    if (yield take(autoRenewChannel)) {
        yield put(updateBillingDataRequest({ radar: 'subs' }));
        onSuccess?.();
        return true;
    }

    autoRenewChannel.close();
    return false;
}

function* turnOnAutopayment(action: ReturnType<typeof BuySubscriptionActions.addAutorenewToSubscription>) {
    yield loadCard();

    const { subscription } = action.payload;

    yield put(BuySubscriptionActions.addAutorenewToSubscription({ subscription }));
}

export function* watchSubscriptionsRenewal() {
    yield takeEvery(SubscriptionRenewalActions.cancelAutoRenew.toString(), handleCancelAutoRenew);
    yield takeEvery(SubscriptionRenewalActions.addAutoRenew.toString(), handleAddAutoRenew);
    // tempexp_18907-next-line
    yield takeEvery(SubscriptionRenewalActions.addAutoRenewWithDiscount.toString(), handleAddAutoRenewWithDiscount);
    yield takeEvery(SubscriptionRenewalActions.turnOnAutopayment.toString(), turnOnAutopayment);
}
